// Passifs des sanctuaires (cumulables à l'infini)
// Les passifs persistent toute la run (mais sont perdus à la mort)

export const PASSIF_RARITIES = {
  D: { nom: "Rang D", couleur: "bg-slate-500", textColor: "text-white", multiplier: 1 },
  C: { nom: "Rang C", couleur: "bg-emerald-500", textColor: "text-white", multiplier: 1.5 },
  B: { nom: "Rang B", couleur: "bg-sky-500", textColor: "text-white", multiplier: 2 },
  A: { nom: "Rang A", couleur: "bg-fuchsia-500", textColor: "text-white", multiplier: 3 },
  S: { nom: "Rang S", couleur: "bg-gradient-to-r from-yellow-400 via-rose-500 to-fuchsia-500", textColor: "text-white", multiplier: 5 },
};

// Trigger types:
//   "room"           — à chaque salle traversée (toute salle)
//   "combat"         — après chaque combat normal
//   "elite_done"     — après chaque élite vaincu (rare, gros bonus)
//   "boss_done"      — après chaque boss vaincu (très rare, énorme bonus)
//   "chest_done"     — après chaque coffre ouvert
//   "sanctuary_done" — après chaque sanctuaire visité
//
// Conditions optionnelles (vérifiées au moment d'appliquer le trigger) :
//   "hp_full"        — uniquement si PV = PVmax au moment du trigger
//   "hp_low"         — uniquement si PV ≤ 30% de PVmax
//   "no_damage_taken"— uniquement si l'on n'a pas pris de dégâts (réservé combat)

export const TRIGGER_LABELS = {
  room: "À chaque salle traversée",
  combat: "Après chaque combat",
  elite_done: "Après chaque élite vaincu",
  boss_done: "Après chaque boss vaincu",
  chest_done: "À chaque coffre ouvert",
  sanctuary_done: "À chaque sanctuaire visité",
};

export const CONDITION_LABELS = {
  hp_full: "si PV au max",
  hp_low: "si PV ≤ 30%",
  no_damage_taken: "si aucun dégât subi",
};

export const PASSIVES_POOL = [
  // ===== Combat triggers (après chaque combat) =====
  { id: "p_combat_atk", nom: "Force Croissante", description: "+{val} ATK après chaque combat", trigger: "combat", stat: "atk", baseValue: 1, icon: "⚔️" },
  { id: "p_combat_def", nom: "Peau de Pierre", description: "+{val} DEF après chaque combat", trigger: "combat", stat: "def", baseValue: 1, icon: "🛡️" },
  { id: "p_combat_pv", nom: "Souffle Vital", description: "+{val} PV max après chaque combat", trigger: "combat", stat: "pv", baseValue: 5, icon: "❤️" },
  { id: "p_combat_prec", nom: "Précision Affinée", description: "+{val}% Précision après chaque combat", trigger: "combat", stat: "precision", baseValue: 1, icon: "🎯" },
  { id: "p_combat_crit", nom: "Œil Critique", description: "+{val}% CRIT après chaque combat", trigger: "combat", stat: "crit", baseValue: 1, icon: "💥" },
  { id: "p_combat_elem", nom: "Volonté Élémentaire", description: "+{val} Dégâts Élémentaires après chaque combat", trigger: "combat", stat: "degatsElem", baseValue: 1, icon: "✨" },

  // ===== Room triggers (à chaque salle) =====
  { id: "p_room_vit", nom: "Pas Léger", description: "+{val} VIT à chaque salle traversée", trigger: "room", stat: "vit", baseValue: 1, icon: "💨" },
  { id: "p_room_res", nom: "Aura Protectrice", description: "+{val} RES à chaque salle traversée", trigger: "room", stat: "res", baseValue: 1, icon: "🌀" },
  { id: "p_room_crit_dmg", nom: "Marche Triomphante", description: "+{val}% Dégâts Crit à chaque salle traversée", trigger: "room", stat: "degatsCrit", baseValue: 1, icon: "🌟" },
  { id: "p_room_heal", nom: "Régénération", description: "Soigne {val} PV à chaque salle traversée", trigger: "room", stat: "heal", baseValue: 2, icon: "💚" },

  // ===== Elite-done (rares, gros bonus) =====
  { id: "p_elite_atk", nom: "Rage du Vainqueur", description: "+{val} ATK après chaque élite vaincu", trigger: "elite_done", stat: "atk", baseValue: 3, icon: "🗡️" },
  { id: "p_elite_pv", nom: "Cœur du Champion", description: "+{val} PV max après chaque élite vaincu", trigger: "elite_done", stat: "pv", baseValue: 15, icon: "💗" },
  { id: "p_elite_crit", nom: "Sang d'Élite", description: "+{val}% CRIT après chaque élite vaincu", trigger: "elite_done", stat: "crit", baseValue: 3, icon: "🩸" },

  // ===== Boss-done (très rares, énormes bonus) =====
  { id: "p_boss_atk", nom: "Couronne de Guerre", description: "+{val} ATK après chaque boss vaincu", trigger: "boss_done", stat: "atk", baseValue: 6, icon: "👑" },
  { id: "p_boss_elem", nom: "Sceau Antique", description: "+{val} Dégâts Élémentaires après chaque boss vaincu", trigger: "boss_done", stat: "degatsElem", baseValue: 6, icon: "🔮" },
  { id: "p_boss_pv", nom: "Souffle du Tyran", description: "+{val} PV max après chaque boss vaincu", trigger: "boss_done", stat: "pv", baseValue: 30, icon: "💀" },

  // ===== Chest-done =====
  { id: "p_chest_def", nom: "Coffre du Veilleur", description: "+{val} DEF à chaque coffre ouvert", trigger: "chest_done", stat: "def", baseValue: 2, icon: "📦" },
  { id: "p_chest_prec", nom: "Œil du Larron", description: "+{val}% Précision à chaque coffre ouvert", trigger: "chest_done", stat: "precision", baseValue: 2, icon: "🔓" },

  // ===== Sanctuary-done =====
  { id: "p_sanct_vit", nom: "Bénédiction du Pèlerin", description: "+{val} VIT à chaque sanctuaire visité", trigger: "sanctuary_done", stat: "vit", baseValue: 2, icon: "🕊️" },
  { id: "p_sanct_pv", nom: "Faveur Divine", description: "+{val} PV max à chaque sanctuaire visité", trigger: "sanctuary_done", stat: "pv", baseValue: 8, icon: "⛪" },

  // ===== Conditionnels (HP full) — récompensent l'agressivité parfaite =====
  { id: "p_full_atk", nom: "Avant-Garde", description: "+{val} ATK après chaque combat si PV au max", trigger: "combat", condition: "hp_full", stat: "atk", baseValue: 2, icon: "🦅" },
  { id: "p_full_crit", nom: "Œil Parfait", description: "+{val}% CRIT après chaque combat si PV au max", trigger: "combat", condition: "hp_full", stat: "crit", baseValue: 2, icon: "🎯" },

  // ===== Conditionnels (HP low) — récompensent les comebacks =====
  { id: "p_low_atk", nom: "Fureur du Désespoir", description: "+{val} ATK après chaque combat si PV ≤ 30%", trigger: "combat", condition: "hp_low", stat: "atk", baseValue: 3, icon: "🔥" },
  { id: "p_low_def", nom: "Résolution Ultime", description: "+{val} DEF après chaque combat si PV ≤ 30%", trigger: "combat", condition: "hp_low", stat: "def", baseValue: 3, icon: "🛡️" },
  { id: "p_low_heal", nom: "Seconde Souffle", description: "Soigne {val} PV à chaque salle si PV ≤ 30%", trigger: "room", condition: "hp_low", stat: "heal", baseValue: 4, icon: "🍃" },

  // ===== Conditionnels (no damage taken) — récompensent la maîtrise =====
  { id: "p_clean_pv", nom: "Combattant Immaculé", description: "+{val} PV max après chaque combat si aucun dégât subi", trigger: "combat", condition: "no_damage_taken", stat: "pv", baseValue: 8, icon: "💎" },
  { id: "p_clean_elem", nom: "Maîtrise Pure", description: "+{val} Dégâts Élémentaires après chaque combat si aucun dégât subi", trigger: "combat", condition: "no_damage_taken", stat: "degatsElem", baseValue: 2, icon: "✨" },

  // ===== Floor triggers (à chaque nouvel étage) — scaling de run =====
  { id: "p_floor_all", nom: "Croissance", description: "+{val} à toutes les stats flat à chaque nouvel étage", trigger: "floor", stat: "all_flat", baseValue: 2, icon: "🌱" },
  { id: "p_floor_pv", nom: "Fortification", description: "+{val} PV max à chaque nouvel étage", trigger: "floor", stat: "pv", baseValue: 12, icon: "🏰" },
  { id: "p_floor_atk", nom: "Ascension Martiale", description: "+{val} ATK à chaque nouvel étage", trigger: "floor", stat: "atk", baseValue: 3, icon: "📈" },

  // ===== Vol de vie léger (après chaque combat) =====
  { id: "p_combat_lifesteal", nom: "Sangsue", description: "Soigne {val} PV après chaque combat", trigger: "combat", stat: "heal", baseValue: 4, icon: "🩸" },

  // ===== Spéciaux (effets non chiffrés, lus ailleurs dans le jeu) =====
  { id: "p_special_cartographe", nom: "Cartographe", description: "Révèle le type des salles derrière les portes", trigger: "passive", special: "reveal_doors", icon: "🗺️" },
  { id: "p_special_econome", nom: "Économe", description: "-{val}% sur tous les prix en boutique", trigger: "passive", special: "shop_discount", baseValue: 8, icon: "💰" },
];

// Génère un passif aléatoire selon la rareté
export const generateSanctuaryPassive = (rarity) => {
  const config = PASSIF_RARITIES[rarity];
  const base = PASSIVES_POOL[Math.floor(Math.random() * PASSIVES_POOL.length)];
  let value = base.baseValue != null ? Math.max(1, Math.round(base.baseValue * config.multiplier)) : 0;
  // L'Économe : on plafonne la remise à 40%
  if (base.special === "shop_discount") value = Math.min(40, value);
  return {
    uuid: `${base.id}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    ...base,
    rarity,
    value,
    description: base.description.replace("{val}", value),
  };
};

// Distribution des raretés des sanctuaires selon l'étage
export const rollSanctuaryRarity = (floor) => {
  const roll = Math.random() * 100;
  const floorBonus = Math.min(floor * 2, 30);
  if (roll < 1 + floorBonus * 0.2) return "S";
  if (roll < 6 + floorBonus * 0.4) return "A";
  if (roll < 20 + floorBonus * 0.5) return "B";
  if (roll < 50 + floorBonus * 0.5) return "C";
  return "D";
};
